<template>
  <div
    class="position-relative"
  >
    <b-button
      v-if="loaded"
      id="trustly-button"
      data-test="trustly-button"
      class="align-middle text-center w-100 px-1"
      @click="handleClick()"
    >
      {{ $t('trustly.button') }}
      <svgicon
        class="no-stroke ml-2"
        icon="trustly-button-mark"
        width="7rem"
        height="2rem"
      />
    </b-button>
    <div
      v-if="!allFieldsValid"
      :id="'trustly-validator' + _uid"
      class="position-absolute fixed-top w-100 h-100"
      @mouseenter="
        // Ask for current state on hover
        getFieldValidation()
      "
      @click="
        // Insist on a re-validation on click.
        // This handles the edge case where all fields were valid, a user
        // invalidates a field, and clicks the button before blurring the field.
        // In that case the field likely won't have validated the new input so
        // the button's hover state (with the cursor still in the offending
        // field) should look okay. When the user clicks though they'll get a
        // full error response from the button and the field.
        getFieldValidation({ touch: true })
      "
    />
    <b-tooltip
      v-if="!allFieldsValid"
      :target="'trustly-validator' + _uid"
      placement="left"
      triggers="click"
      :title="$t('third_party_payment.error.fields_required')"
    />
  </div>
</template>

<script setup>
import EventBus from '@grantstreet/psc-vue/utils/event-bus.ts'
import '@grantstreet/bootstrap/icons/js/trustly-button-mark.js'
import { isProd } from '@grantstreet/psc-environment'
import { onMounted, onBeforeUnmount, computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const props = defineProps({
  error: {
    type: Function,
    default: async () => {},
  },
  cartHasDelayedPayments: {
    type: Boolean,
    required: true,
  },
  cartTotal: {
    type: [String, Number],
    default: '',
  },
  paymentData: {
    type: Object,
    required: true,
  },
  trustlyConfig: {
    type: Object,
    required: true,
  },
})
const store = useStore()
const emit = defineEmits(['trustly-error'])

const sources = [
  'contactInfo',
  'policyAgreement',
  'trustlyData',
]

const isValid = ref(Object.fromEntries(sources.map(source => [source, false])))
const loaded = ref(false)
const handlingClick = ref(false)
const validationHandlers = ref(Object.fromEntries(
  sources.map(source => [source, valid => {
    isValid.value[source] = valid
  }]),
))

const allFieldsValid = computed(() => {
  return isValid.value.contactInfo && isValid.value.policyAgreement && !props.cartHasDelayedPayments && isValid.value.trustlyData
})

const loadScript = () => {
  let el = document.querySelector('#trustly-sdk')

  if (el) {
    // If it's already loaded then just use it
    if (typeof Trustly !== 'undefined') {
      loaded.value = true
      return
    }
    // Otherwise respectfully wait
    el.addEventListener('load', () => {
      el.setAttribute('data-loaded', 'true')
      loaded.value = true
    })
    return
  }

  el = document.createElement('script')
  // The accessID is a public key and can be stored in the FE.
  el.src = isProd()
    ? 'https://trustly.one/start/scripts/trustly.js?accessId=ADugIuOxN6JOVG3DkXBe'
    : 'https://sandbox.trustly.one/start/scripts/trustly.js?accessId=ADugIuOxN6JOVG3DkXBe'
  el.addEventListener('load', () => {
    el.setAttribute('data-loaded', 'true')
    loaded.value = true
  })
  el.id = 'trustly-sdk'
  document.head.appendChild(el)
}

const addTrustlyListeners = () => {
  Trustly.addPanelListener((command, obj) => {
    if (command === 'event' && obj.type === 'new_location') {
      const location = obj.data
      if (location.startsWith('#success')) {
        handleSuccess(obj.data)
      }
      else {
        Trustly.removeListeners()
      }
    }
  })
}

// Requests current state of field validation. Does not ask respondent to
// re-validate unless touch: true is passed
const getFieldValidation = (touch) => {
  EventBus.$emit('ewallet.trustlyValidation', { touch })
}

const handleClick = async () => {
  if (!handlingClick.value) {
    handlingClick.value = true
    try {
      addTrustlyListeners()
      const trustlySetupInfo = await store.dispatch('eWallet/createTrustlySetup', {
        amount: props.cartTotal,
      })
      Trustly.establish({
        merchantId: trustlySetupInfo.merchant_id,
        accessId: trustlySetupInfo.access_id,
        requestSignature: trustlySetupInfo.request_signature,
        merchantReference: trustlySetupInfo.request_id,
        paymentType: 'Retrieval',
        currency: 'USD',
        amount: props.cartTotal,
        returnUrl: '#success',
        cancelUrl: '#cancel',
        metadata: {
          lang: i18n.locale === 'es' ? 'es_US' : 'en_US',
        },
      })
      // The Trustly Modal tries to set the top level height 100% which messes with our auto sizing in the checkout widget. Remove it.
      document.documentElement.style.height = ''
    }
    catch (error) {
      errorHandler(error)
    }
    finally {
      handlingClick.value = false
    }
  }
}

const handleSuccess = async (urlHash) => {
  // [0] is the full string
  // [1] is the capture group
  const transactionId = urlHash.match(/transactionId=([^&]+)/)[1]
  try {
    // First we need to validate this success actually came from Trustly
    validateSuccess(urlHash)
    await props.trustlyConfig.transactionApproved(transactionId, props.paymentData)
  }
  catch (error) {
    errorHandler(error)
  }
  finally {
    Trustly.removeListeners()
  }
}

// Validate success throws an error if the urlHash doesn't line up with what we expect
// from Trustly.
const validateSuccess = (urlHash) => {
  // TODO: Pass the query params to Paycore for validation
}

const errorHandler = (error) => {
  if (error.displayMessage) {
    emit('trustly-error', error.displayMessage)
  }
  else {
    emit('trustly-error', error)
  }
}

onMounted(() => {
  loadScript()

  // Sets up EventBus listeners
  Object.keys(validationHandlers.value).forEach(source =>
    EventBus.$on(`ewallet.validation.${source}`, validationHandlers.value[source]),
  )

  getFieldValidation()
})

onBeforeUnmount(() => {
  Object.keys(validationHandlers.value).forEach(source =>
    EventBus.$off(`ewallet.validation.${source}`, validationHandlers.value[source]),
  )
  Trustly.removeListeners()
})

</script>

<style lang="scss" scoped>

  .pe-none {
    pointer-events: none !important;
  }

  #trustly-button {
    background-color: black !important;
    color: white !important;
  }

  #trustly-button:hover {
    opacity: 0.7;
  }

  #trustly-validator:hover {
    cursor: "pointer";
  }
</style>
